import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { MainContentContainer } from '@ctw/shared/components/layout/main-content-container';
import { MainContentWithDockableDrawer } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { SubNavigationBar } from '@ctw/shared/components/navigation/sub-navigation-bar';
import { VerticalSubNavigationDock } from '@ctw/shared/components/navigation/vertical-sub-navigation-dock';
import { AlertFeatureFlag } from '@ctw/shared/content/alert-feature-flag';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import {
  PatientSubNavigationProvider,
  usePatientSubNavigation,
} from '@ctw/shared/context/patient-sub-navigation-context';
import { usePatientHeaderFields } from '@ctw/shared/hooks/use-patient-header-fields';
import { useZapSections } from '@ctw/shared/hooks/use-zap-sections';
import '@ctw/shared/styles/preflight';
import { PageError } from '@ctw/shared/components/errors/page-error';
import { PatientHistoryStatus } from '@ctw/shared/content/patient-history-status';
import { PatientRecordSearchBar } from '@ctw/shared/content/patient-record-search-fqs/patient-record-search-bar';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { tw } from '@ctw/shared/utils/tailwind';
import { trimEnd, trimStart } from 'lodash-es';
import { Fragment, type PropsWithChildren, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';

export type ZAPTabName = Readonly<(typeof ZAP_TAB_NAMES)[number]>;

export const ZAP_TAB_NAMES = [
  'summary',
  'allergies',
  'care-team',
  'conditions-all',
  'diagnostic-reports',
  'documents',
  'encounters',
  'referrals',
  'immunizations',
  'medications-all',
  'search',
  'overview',
  'demographics',
  'vitals',
] as const;

export const ZusAggregatedProfile = () => {
  const { requestContext } = useCTW();
  const zapSections = useZapSections();
  const maximumPathSegmentsForFallbackRouteRedirect = ['salesforce', 'vim'].includes(
    requestContext.ehr,
  )
    ? 3
    : 2;

  const fallbackRoute = useMemo(() => {
    const url = new URL(window.location.href);
    url.pathname = `${trimEnd(url.pathname, '/')}/${trimStart(zapSections[0].href, '/')}`;
    return `${url.pathname}${url.search}${url.hash}`;
  }, [zapSections]);

  return (
    <ErrorBoundary boundaryName="ZAP Router">
      {() => (
        <PatientSubNavigationProvider subNavigationLinks={zapSections}>
          <AlertFeatureFlag featureFlag="ctw-zap-alert" />
          <PatientAggregatedProfile>
            <Routes>
              {zapSections.map((section) => (
                <Fragment key={section.href}>
                  <Route path={section.href} Component={section.component} />
                  {section.siblingHrefs?.map((siblingHref) => (
                    <Route key={siblingHref} path={siblingHref} Component={section.component} />
                  ))}
                </Fragment>
              ))}
              <Route
                index={true}
                path="*"
                element={
                  window.location.pathname.split('/').length >
                  maximumPathSegmentsForFallbackRouteRedirect ? (
                    <PageError title="Page not found" />
                  ) : (
                    <Navigate to={fallbackRoute} replace={true} />
                  )
                }
              />
            </Routes>
          </PatientAggregatedProfile>
        </PatientSubNavigationProvider>
      )}
    </ErrorBoundary>
  );
};

interface PatientAggregatedProfile extends PropsWithChildren {}

const PatientAggregatedProfile = ({ children }: PatientAggregatedProfile) => {
  const { patient } = usePatientContext();
  const subNavigation = usePatientSubNavigation();
  const headerFields = usePatientHeaderFields();
  const { breakpoints } = useBreakpointQuery();
  const handleContentLoadingState = useMemo(
    () => subNavigation?.activeLink?.siblingHrefs === undefined,
    [subNavigation],
  );
  const hasPatientRecordSearchPocFeature = useFeatureFlag('ctw-patient-record-search');
  const hasPatientRecordSearchFqsFeature = useFeatureFlag('ctw-patient-record-search-fqs');

  return (
    <>
      <SubNavigationBar
        avatar={{
          name: `${patient.firstName} ${patient.lastName}`,
        }}
        headerFields={headerFields}
        subNavigationLinks={subNavigation.links}
        renderContent={{
          afterHeaderFields: () => (
            <>
              <PatientHistoryStatus expanded={breakpoints.isAtLeast.lg} inline={true} />

              {(hasPatientRecordSearchFqsFeature || hasPatientRecordSearchPocFeature) && (
                <div className={tw`flex flex-grow flex-row items-center justify-end`}>
                  <PatientRecordSearchBar />
                </div>
              )}
            </>
          ),
        }}
      />
      <MainContentContainer>
        <VerticalSubNavigationDock navigationLinks={subNavigation.links} />
        <ErrorBoundary boundaryName="ZAP Main Content">
          {() => (
            <MainContentWithDockableDrawer handleContentLoadingState={handleContentLoadingState}>
              {children}
            </MainContentWithDockableDrawer>
          )}
        </ErrorBoundary>
      </MainContentContainer>
    </>
  );
};
